import {
  Box,
  TextField,
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@mui/material';
import FormMonitoring from 'components/FormMonitoring';
import { CommonErrorsTexts, CommonTexts, ConfigFormsLabels } from 'locale/es';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmptyObj } from 'utils/HelperFunctions';
import { toast } from 'redux/reducers/notificationReducer';
import SingleChoiceDropdown from 'components/Dropdowns/SingleChoiceDropdown';
import Loader from 'components/Loader/Loader';
import ErrorPage from 'pages/ErrorPage';
import DatePickerMonitoring from 'components/DatePickers/DatePickerMonitoring/DatePickerMonitoring';
import { AppColors } from 'resources/AppColors';
import { makeStyles } from '@mui/styles';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';

const useStyles = makeStyles((theme) => ({
  textFieldLabel: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: AppColors.PRIMARY,
      },
    },
    '& .MuiInputBase-root': {
      color: AppColors.PRIMARY,
    },
    '& .MuiInputLabel-root': {
      color: AppColors.PRIMARY,
    },
    color: AppColors.PRIMARY,
    backgroundColor: AppColors.BOX_FILL,
  },
}));

const descriptionType = z.string().superRefine((val, ctx) => {
  if (val.length === 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.too_small,
      minimum: 2,
      type: 'string',
      inclusive: true,
      message: ConfigFormsLabels.formInputErrors.description,
    });
  }

  return val;
});

const newFormSchema = z
  .object({
    startDate: z
      .date({
        errorMap: (issue, { defaultError }) => ({
          message:
            issue.code === 'invalid_date'
              ? CommonErrorsTexts.date.invalidDate
              : issue.code === 'invalid_type'
              ? CommonErrorsTexts.date.date
              : defaultError,
        }),
      })
      .optional()
      .nullable(),
    endDate: z
      .date({
        errorMap: (issue, { defaultError }) => ({
          message:
            issue.code === 'invalid_date'
              ? CommonErrorsTexts.date.invalidDate
              : issue.code === 'invalid_type'
              ? CommonErrorsTexts.date.date
              : defaultError,
        }),
      })
      .optional()
      .nullable(),
    studyId: z.object({
      id: z.number(),
      title: z.string(),
    }),
    priority: z.string(),
    formId: z.number({
      required_error: CommonErrorsTexts.required,
    }),
    formTag: z
      .string({ required_error: CommonErrorsTexts.required })
      .min(2, ConfigFormsLabels.formInputErrors.tag),
    formDescription: descriptionType,
  }).refine((data) => data.startDate <= data.endDate, {
    message: ConfigFormsLabels.formInputErrors.end_date,
    path: ["endDate"],
  })
  .transform((data) => ({
    ...data,
    startDate: data.startDate
      ? format(data.startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
      : null,
    endDate: data.endDate
      ? format(data.endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
      : null,
    studyId: data.studyId.id,
  }));

const FormForm = ({
  entityId,
  selectedFormTag,
  isEdit = false,
  setShowModal,
  dataForm,
  onSubmit,
}) => {
  const [formError, setFormError] = useState('');

  const {
    data: studyData,
    loading: studyLoading,
    error: studyError,
  } = useSelector(({ study }) => study);

  const dispatch = useDispatch();

  const classes = useStyles();

  const radioButtonOptions = [
    { label: ConfigFormsLabels.IMPORTANT, value: 'IMPORTANT' },
    { label: ConfigFormsLabels.REQUIRED, value: 'REQUIRED' },
    { label: ConfigFormsLabels.NOT_REQUIRED, value: 'NOT_REQUIRED' },
  ];

  const formToEdit = dataForm.find((form) => form.formTag === selectedFormTag);

  const { handleSubmit, control, reset, getValues, watch } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: zodResolver(newFormSchema),
    resetOptions: {
      keepDefaultValues: true,
    },
    defaultValues: {
      endDate: formToEdit?.endDate ? new Date(formToEdit?.endDate) : null,
      formDescription: formToEdit?.formDescription || '',
      formId: formToEdit?.formId || '',
      formTag: formToEdit?.formTag || '',
      priority: formToEdit?.priority || '',
      startDate: formToEdit?.startDate ? new Date(formToEdit?.startDate) : null,
      studyId: studyData,
    },
  });

  const startDateWatch = watch('startDate');

  async function handleOnConfirmSubmit(data) {
    try {
      const formTagValue = getValues().formTag;
      const sanityCheckFormTag = dataForm.find(
        (form) => form.formTag === formTagValue
      );

      const formName = studyData.forms.find(
        (form) => form?.value === getValues().formId
      )?.name;

      if (!formName) {
        dispatch(toast('Unable to find the selected form name', 'error'));
      }
      const formObject = {
        ...data,
        formName,
        id: isEdit ? formToEdit.id && { id: formToEdit.id } : null,
      };

      // * When form is created and no exists other form with same tag, the action is dispach if not it gives an error
      if (!isEdit && isEmptyObj(sanityCheckFormTag)) {
        await dispatch(onSubmit(formObject, entityId));
        setShowModal(false);

      // * If form is edited the tag isn´t compare
      } else if (isEdit) {
        await dispatch(onSubmit(formObject, entityId));
        setShowModal(false);

      } else {
        setFormError(ConfigFormsLabels.formInputErrors.repeated_formTag);
        console.log('error');
      }
    } catch (error) {
      setFormError(error);
    }
  }

  if (studyLoading) {
    return <Loader message={'Loading study and forms...'} pt={1} />;
  }

  if (!studyData) {
    return <ErrorPage message='This center has no associated study' />;
  }

  return (
    <FormMonitoring
      submitForm={handleSubmit(handleOnConfirmSubmit)}
      resetFormInputs={reset}
      isEdit={isEdit}
    >
      <Box
        fullwidth='true'
        sx={{ display: 'flex', justifyContent: 'space-around' }}
      >
        <Box sx={{ maxWidth: 160 }}>
          <Controller
            control={control}
            name='startDate'
            render={({
              field: { onChange, onBlur, value, ref },
              fieldState: { error },
            }) => (
              <FormControl className={classes.textFieldLabel}>
                <DatePickerMonitoring
                  label={ConfigFormsLabels.START_DATE}
                  maxDate={new Date()}
                  inputRef={ref}
                  value={value}
                  setValue={onChange}
                  textfieldProps={{
                    error: Boolean(error),
                    helperText: error?.message ?? '',
                    onBlur: onBlur,
                  }} />
              </FormControl>
            )}
          />
        </Box>
        <Box sx={{ maxWidth: 160 }}>
          <Controller
            control={control}
            name='endDate'
            render={({
              field: { onChange, onBlur, value, ref },
              fieldState: { error },
            }) => (
              <FormControl className={classes.textFieldLabel}>
                <DatePickerMonitoring
                  label={ConfigFormsLabels.END_DATE}
                  minDate={startDateWatch ? startDateWatch : new Date()}
                  inputRef={ref}
                  value={value}
                  setValue={onChange}
                  textfieldProps={{
                    error: Boolean(error),
                    helperText: error?.message ?? '',
                    onBlur: onBlur,
                  }}
                />
              </FormControl>
            )}
          />
        </Box>
      </Box>
      <Controller
        control={control}
        name='studyId'
        render={({ field: { value } }) => (
          <TextField
            value={value.title}
            type='text'
            disabled
            label={ConfigFormsLabels.STUDY_NAME}
            margin='normal'
            variant='outlined'
            className={classes.textFieldLabel}
          />
        )}
      />
      <Controller
        control={control}
        name='priority'
        render={({ field: { value, onChange } }) => (
          <FormControl>
            <FormLabel
              sx={{ color: AppColors.PRIMARY, mt: 2 }}
              id='demo-row-radio-buttons-group-label'
            >
              <Typography variant='subtitle_bold' mt={2}>
                {ConfigFormsLabels.PRIORITY}
              </Typography>
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby='demo-row-radio-buttons-group-label'
              name='row-radio-buttons-group'
              value={value}
              onChange={onChange}
            >
              {radioButtonOptions.map((option) => (
                <FormControlLabel
                  key={option.label}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name='formId'
        render={({
          field: { onChange, onBlur, value },
          fieldState: { error },
        }) => (
          <>
            <SingleChoiceDropdown
              labelRequired={true}
              label={ConfigFormsLabels.FORM_NAME}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              items={studyData.forms}
              error={error?.message ?? ''}
            />
            <FormHelperText
              id='my-helper-text'
              error={Boolean(error)}
              className={classes.textFieldLabel}
              sx={{ mt: 0, pl: 2 }}
            >
              {error && CommonErrorsTexts.required}
            </FormHelperText>
          </>
        )}
      />
      <Controller
        control={control}
        name='formTag'
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            type='text'
            InputLabelProps={{
              required: true,
            }}
            disabled={isEdit}
            label={ConfigFormsLabels.TAG}
            margin='normal'
            variant='outlined'
            className={classes.textFieldLabel}
            error={Boolean(error)}
            helperText={error?.message ?? ''}
          />
        )}
      />
      <Controller
        control={control}
        name='formDescription'
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            type='text'
            label={CommonTexts.DESCRIPTION}
            margin='normal'
            variant='outlined'
            className={classes.textFieldLabel}
            error={Boolean(error)}
            helperText={error?.message ?? ''}
          />
        )}
      />
      {formError && (
        <Typography variant='body' sx={{ color: 'red' }}>
          {formError === ConfigFormsLabels.formInputErrors.repeated_formTag
            ? formError
            : ConfigFormsLabels.formInputErrors.api_error}
        </Typography>
      )}
    </FormMonitoring>
  );
};

export default FormForm;
